/**
 * Servicios catalog management
 */

import {
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Icon, Input } from "@/components/ui";
import { servicesService } from "@/services/servicesService";
import { formatCurrency } from "@/utils/formatters";
import { notifyAction } from "@/utils";
import styles from "./ServicesPage.module.css";

interface Service {
  id: string;
  name: string;
  price: number;
  lastUpdated: string;
}

interface FormState {
  name: string;
  price: string;
}

const formatDate = (iso: string): string =>
  new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "short",
  }).format(new Date(iso));

export function ServicesPage(): ReactElement {
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formState, setFormState] = useState<FormState>({
    name: "",
    price: "",
  });
  const [formErrors, setFormErrors] = useState<Partial<FormState>>({});
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await servicesService.list({ page_size: 100 });
        const mapped: Service[] = res.results.map((s) => ({
          id: String(s.id),
          name: s.name,
          price: parseFloat(s.price),
          lastUpdated: new Date().toISOString(),
        }));
        setServices(mapped);
        if (mapped.length > 0) setSelectedService(mapped[0]);
      } catch (err) {
        setError("Error al cargar los servicios");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    void fetchServices();
  }, []);

  const filteredServices = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return services;
    }
    return services.filter((service) =>
      service.name.toLowerCase().includes(term),
    );
  }, [services, searchTerm]);

  const resetForm = () => {
    setFormState({ name: "", price: "" });
    setFormErrors({});
    setEditingServiceId(null);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const openCreateForm = () => {
    resetForm();
    setFormVisible(true);
  };

  const openEditForm = (service: Service) => {
    setFormState({ name: service.name, price: service.price.toString() });
    setFormErrors({});
    setEditingServiceId(service.id);
    setFormVisible(true);
  };

  const closeForm = () => {
    resetForm();
    setFormVisible(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = formState.name.trim();
    const normalizedPriceInput = formState.price.replace(",", ".");
    const parsedPrice = Number.parseFloat(normalizedPriceInput);

    const errors: Partial<FormState> = {};
    if (!trimmedName) {
      errors.name = "Introduce un nombre";
    }
    if (Number.isNaN(parsedPrice)) {
      errors.price = "Introduce un precio válido";
    } else if (parsedPrice < 0) {
      errors.price = "El precio debe ser positivo";
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const normalizedPrice = Number(parsedPrice.toFixed(2));

    if (editingServiceId) {
      await servicesService.update(Number(editingServiceId), {
        name: trimmedName,
        price: String(normalizedPrice),
      });
      const updatedServices = services.map((service) =>
        service.id === editingServiceId
          ? {
              ...service,
              name: trimmedName,
              price: normalizedPrice,
              lastUpdated: new Date().toISOString(),
            }
          : service,
      );
      setServices(updatedServices);
      if (selectedService?.id === editingServiceId) {
        const nextSelected = updatedServices.find(
          (service) => service.id === editingServiceId,
        );
        setSelectedService(nextSelected ?? null);
      }
    } else {
      const apiResult = await servicesService.create({
        name: trimmedName,
        price: String(normalizedPrice),
      });
      const newService: Service = {
        id: String(apiResult.id),
        name: trimmedName,
        price: normalizedPrice,
        lastUpdated: new Date().toISOString(),
      };
      const updatedServices = [...services, newService];
      setServices(updatedServices);
      setSelectedService(newService);
    }

    closeForm();
  };

  const deleteService = async (service: Service) => {
    await servicesService.remove(Number(service.id));
    const serviceId = service.id;
    const updatedServices = services.filter(
      (service) => service.id !== serviceId,
    );
    setServices(updatedServices);
    if (selectedService?.id === serviceId) {
      setSelectedService(updatedServices[0] ?? null);
    }
    if (editingServiceId === serviceId) {
      closeForm();
    }
  };

  const requestDelete = (service: Service) => {
    notifyAction({
      title: "Eliminar servicio",
      description: `Se eliminará \"${service.name}\" del catálogo.`,
      buttonTitle: "Eliminar",
      onClick: () => {
        void deleteService(service);
      },
    });
  };

  if (isLoading)
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p>Cargando servicios...</p>
        </div>
      </div>
    );
  if (error)
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p>{error}</p>
        </div>
      </div>
    );

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div>
            <p className={styles.breadcrumb}>Catálogo · Servicios</p>
            <h1 className={styles.title}>Servicios disponibles</h1>
            <p className={styles.subtitle}>
              Gestiona el listado que aparecerá en los presupuestos. Mantén los
              precios actualizados para que el selector muestre siempre importes
              vigentes.
            </p>
          </div>
          <Button onClick={openCreateForm} className={styles.primaryAction}>
            <Icon name="plus" size={18} />
            Nuevo servicio
          </Button>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Buscar servicio"
              value={searchTerm}
              onChange={handleSearchChange}
              leftElement={<Icon name="search" size={16} />}
              fullWidth
            />
          </div>
          <span className={styles.resultCount}>
            {filteredServices.length} servicios
          </span>
        </div>

        <div className={styles.content}>
          <div className={styles.listSection}>
            {filteredServices.length > 0 ? (
              filteredServices.map((service) => (
                <button
                  type="button"
                  key={service.id}
                  className={`${styles.serviceItem} ${selectedService?.id === service.id ? styles.activeItem : ""}`}
                  onClick={() => setSelectedService(service)}
                >
                  <div className={styles.serviceInfo}>
                    <p className={styles.serviceName}>{service.name}</p>
                    <p className={styles.serviceMeta}>
                      Actualizado {formatDate(service.lastUpdated)}
                    </p>
                  </div>
                  <div className={styles.serviceActions}>
                    <span className={styles.servicePrice}>
                      {formatCurrency(service.price)}
                    </span>
                    <div className={styles.actionButtons}>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditForm(service);
                        }}
                        title="Editar servicio"
                      >
                        <Icon name="edit" size={16} />
                      </button>
                      <button
                        type="button"
                        className={`${styles.iconButton} ${styles.deleteButton}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          requestDelete(service);
                        }}
                        title="Eliminar servicio"
                      >
                        <Icon name="trash" size={16} />
                      </button>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className={styles.emptyList}>
                <Icon name="search" size={20} />
                <p>No hay servicios que coincidan con la búsqueda.</p>
              </div>
            )}
          </div>

          <div className={styles.panel}>
            {formVisible ? (
              <form className={styles.formCard} onSubmit={handleSubmit}>
                <div className={styles.formHeader}>
                  <h3>
                    {editingServiceId ? "Editar servicio" : "Nuevo servicio"}
                  </h3>
                  <p>
                    Este registro se usará dentro del selector de presupuestos.
                  </p>
                </div>
                <Input
                  label="Nombre"
                  placeholder="Ej. Tratamiento mensual"
                  value={formState.name}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      name: event.target.value,
                    }))
                  }
                  error={formErrors.name}
                  required
                  fullWidth
                />
                <Input
                  label="Precio"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formState.price}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      price: event.target.value,
                    }))
                  }
                  error={formErrors.price}
                  helperText="Se mostrará en euros (€)."
                  required
                  fullWidth
                />
                <div className={styles.formActions}>
                  <Button type="button" variant="ghost" onClick={closeForm}>
                    Cancelar
                  </Button>
                  <Button type="submit">
                    {editingServiceId ? "Guardar cambios" : "Crear servicio"}
                  </Button>
                </div>
              </form>
            ) : selectedService ? (
              <div className={styles.detailCard}>
                <div className={styles.detailHeader}>
                  <p className={styles.detailEyebrow}>Servicio seleccionado</p>
                  <h3>{selectedService.name}</h3>
                </div>
                <p className={styles.detailPrice}>
                  {formatCurrency(selectedService.price)}
                </p>
                <p className={styles.detailMeta}>
                  Actualizado {formatDate(selectedService.lastUpdated)}.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => openEditForm(selectedService)}
                >
                  Actualizar precio
                </Button>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>Selecciona un servicio de la lista o crea uno nuevo.</p>
                <Button variant="secondary" onClick={openCreateForm}>
                  Crear servicio
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServicesPage;
